import { useRef, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover"
import { cn } from "../lib/utils"

export function PopoverRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults: Popover root is a provider; surface atom
  // contract on a wrapping span (same pattern as dialog/sheet/dropdown).
  const atomDefaults = useAtomDefaults("popover")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // ── trigger / content slots ────────────────────────────────────────────────
  // Type-based first, position second. Two things the positional arm has to
  // respect, both measured on the emitted HTML rather than on these props:
  //
  //  1. It must not re-pick the node already claimed as the panel. Unguarded
  //     `allChildren[0]` makes `trigger` and `contentWrapper` the SAME object
  //     whenever the content wrapper is written first. `popover-content` has
  //     no registered renderer (it is not in cogo-validator/ontology/
  //     element-types.json), so feeding it to ElementRenderer emits a
  //     `data-unknown-type="popover-content"` placeholder — the node the
  //     measured-tier harness counts — and, because the panel is then taken
  //     from that wrapper's children, every sibling beside it is dropped with
  //     no diagnostic. `[popover-content, button]` painted 556 bytes with the
  //     button nowhere in the document, open or closed.
  //  2. A typed wrapper is a slot, not an element. `popover-trigger` is
  //     likewise unregistered, so rendering the wrapper itself swallowed the
  //     button inside it behind a placeholder (`[popover-trigger,
  //     popover-content]`: 558 bytes, trigger label absent).
  //
  // Same shape as sheet-renderer.tsx, which reached these two conclusions
  // first; the sole-child case deliberately differs (see below).
  const allChildren = element.children ?? []
  const contentWrapper = allChildren.find(c => c.type === "popover-content")
  const trigger =
    allChildren.find(c => c.type === "popover-trigger")
    // Unlike the sheet, a lone untyped child stays the trigger: the popover's
    // trigger is the only thing it paints while closed, so demoting it to
    // panel body would make a one-child popover paint nothing at all. The
    // invariant both renderers share is the one below — trigger and panel are
    // disjoint, and every authored child lands in exactly one of them.
    ?? allChildren.find(c => c !== contentWrapper)
  const triggerIsTypedSlot = trigger?.type === "popover-trigger"
  const content = contentWrapper
    // The wrapper's children ARE the panel; anything else the author wrote
    // beside it (other than the trigger) follows them instead of vanishing.
    ? [
        ...(contentWrapper.children ?? []),
        ...allChildren.filter(c => c !== contentWrapper && c !== trigger),
      ]
    : allChildren.filter(c => c !== trigger)

  // A typed slot is a wrapper — render what it wraps, and carry the slot's own
  // id here, because nothing else renders it. A positionally-picked child is a
  // real element that ElementRenderer already stamps with its own
  // `data-cogo-element-id`, and every consumer of the identity contract
  // resolves an id by querySelector — stamping it here too would give one id
  // two nodes.
  const triggerContent = !trigger
    ? null
    : triggerIsTypedSlot
      ? (trigger.children ?? []).map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))
      : <ElementRenderer element={trigger} />
  const triggerIdentity = triggerIsTypedSlot && trigger
    ? { "data-cogo-element-id": trigger.id, "data-cogo-type": trigger.type }
    : {}

  // defaultOpen: force-open uncontrolled state (preview / initial-open use cases).
  // Matches the pattern already established on alert-dialog-renderer.tsx and
  // collapsible-renderer.tsx. `variant` is orthogonal here (dialog renderer
  // overloads variant="open" but this collides with matrix-cell variant
  // overrides in DS Browser previews).
  const rawOpen = (element as unknown as { open?: unknown }).open
    ?? (element as unknown as { defaultOpen?: unknown }).defaultOpen
  const resolvedOpen = typeof rawOpen === "string" ? resolveSymbol(rawOpen) : rawOpen
  const requestedOpen = resolvedOpen === true || resolvedOpen === "true"
  const [isOpen, setIsOpen] = useState(requestedOpen)
  const openRef = useRef(requestedOpen)
  const previousElementRef = useRef(element)

  if (previousElementRef.current !== element && rawOpen !== undefined) {
    previousElementRef.current = element
    openRef.current = requestedOpen
    if (isOpen !== requestedOpen) setIsOpen(requestedOpen)
  }

  const handleOpenChange = (open: boolean) => {
    if (openRef.current === open) return
    openRef.current = open
    setIsOpen(open)
    dispatchEvent(element.id, open ? "open" : "close", {})
  }

  return (
    <span
      data-atom-type="popover"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-is-open={isOpen ? "true" : "false"}
      data-testid={`popover-wrapper-${element.id}`}
      aria-expanded={isOpen}
      className={cn("inline-block", element.className)}
      onClickCapture={() => handleOpenChange(!openRef.current)}
    >
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        {/* base-ui uses `render` (ReactElement) for composition; asChild is a Radix
            pattern and was silently ignored here pre-migration. The rendered
            element receives merged props (ref, aria, data-*) from PopoverTrigger. */}
        <PopoverTrigger
          nativeButton={false}
          className="cn-popover-trigger cursor-pointer"
          data-testid={`popover-${element.id}`}
          {...triggerIdentity}
          render={<div>{triggerContent}</div>}
        />
        {content && content.length > 0 && (
          <PopoverContent>
            {content.map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
          </PopoverContent>
        )}
      </Popover>
    </span>
  )
}
